/** @jsxRuntime automatic */
/** @jsxImportSource @fluentui/react-jsx-runtime */

import { assertSlots, makeStyles } from "@fluentui/react-components";

import { useV9MenuEnabled, type AppContextualMenuProps } from "@1js/acui-menu";

import type {
  AppFlyoutAnchorProps,
  AppFlyoutAnchorSlots,
  AppFlyoutAnchorState,
} from "./AppFlyoutAnchor.types";
import { MenuContextProviders } from "./MenuContextProviders";
import { AppTooltipContainer } from "@1js/acui-tooltip";
import { ReactUiPaintTiming } from "@1js/acui-paint-timing";
import { useAsyncUiTracking } from "../../../hooks/useAsyncUITracking";
import * as React from "react";

const useAppFlyoutAnchorStyles = makeStyles({
  root: {
    visibility: "hidden",
  },
});

export const renderAppFlyoutAnchor = (
  state: AppFlyoutAnchorState,
  props: AppFlyoutAnchorProps
) => {
  assertSlots<AppFlyoutAnchorSlots>(state);
  const {
    shadowRenderMenu,
    isShadowRendering,
    persistMenu,
    menuAnchorContextValue,
  } = state;

  const { id, location } = props;
  // VSO:5851658 see if we can remove the cast for menu, typescript
  // says the union type is too complex
  const shouldRenderMenu =
    !(state.menu as AppContextualMenuProps).hidden ||
    (shadowRenderMenu && isShadowRendering) ||
    persistMenu;
  const shouldTrackAsyncUI =
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAsyncUiTracking();

  /**
   * When the v9 menu is being rendered, this applies a class to hide the tooltip.
   * This prevents the tooltip from appearing when the v9 menu is open, avoiding UI overlap.
   */
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const toolTipHiddenClass = useAppFlyoutAnchorStyles().root;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const isV9MenuEnabled = useV9MenuEnabled();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [visible, setVisible] = React.useState(false);
  return (
    <div
      onMouseLeave={(_) => setVisible(false)}
      onFocus={(_) => state.menu.hidden && setVisible(true)}
      onBlur={(_) => setVisible(false)}
    >
      {shouldTrackAsyncUI && (
        <ReactUiPaintTiming
          elementTimingId={id}
          hidden={(state.menu as AppContextualMenuProps).hidden}
          interactivityTrackerContext={`${id}_${location}`}
        />
      )}
      <MenuContextProviders
        isShadowRendering={isShadowRendering}
        menuAnchorContextValue={menuAnchorContextValue}
      >
        <>
          <AppTooltipContainer
            {...props}
            showTooltip={props.showTooltip}
            visible={visible}
            {...(isV9MenuEnabled &&
              shouldRenderMenu && { tooltipClassName: toolTipHiddenClass })}
          >
            <state.menuButton />
          </AppTooltipContainer>
          {shouldRenderMenu && <state.menu />}
        </>
      </MenuContextProviders>
    </div>
  );
};
